import React from "react";
import { useFieldArray } from "react-hook-form";
import { ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";
import { Button } from "../../../../../ui/button";

/**
 * Shared add/remove/reorder block for list-shaped section fields.
 *
 * Reorder is implemented with simple up/down buttons rather than @dnd-kit so
 * it works at any viewport and stays keyboard-accessible without extra wiring.
 *
 * Props:
 *  - form: RHF form instance
 *  - name: dotted path to the array field
 *  - label: visible label for the list
 *  - max: maximum number of entries
 *  - newItem: factory returning a default new entry
 *  - renderItem: ({ index, form, remove, swap, isFirst, isLast }) => JSX
 */
const ListArrayEditor = ({
  form,
  name,
  label,
  max,
  newItem,
  renderItem,
  emptyText,
}) => {
  const { fields, append, remove, swap } = useFieldArray({
    control: form.control,
    name,
  });

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append(newItem())}
          disabled={typeof max === "number" && fields.length >= max}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          {emptyText || "No entries yet."}
        </p>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="rounded-md border bg-muted/20 px-3 py-3 space-y-3"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-3">
                  {renderItem({ index, form })}
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => index > 0 && swap(index, index - 1)}
                    disabled={index === 0}
                    aria-label="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      index < fields.length - 1 && swap(index, index + 1)
                    }
                    disabled={index === fields.length - 1}
                    aria-label="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => remove(index)}
                    aria-label="Remove"
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {typeof max === "number" && (
        <p className="text-xs text-muted-foreground">
          {fields.length} / {max} entries
        </p>
      )}
    </div>
  );
};

export default ListArrayEditor;
